import { useLocation, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { StatusChip } from '../components/StatusChip';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import type { Classifier } from '@zg/shared/property';

/**
 * Short-circuit route for prospects whose classifier falls outside the
 * self-serve v1 scope: portfolios with multiple buildings, or very large
 * accounts (TIV > $100M). See docs/property/PRD.md §6.5 and §13.
 */
export function PropertyContactBroker() {
  const location = useLocation();
  const state = (location.state as { classifier?: Classifier } | null) ?? null;
  const reason = buildReason(state?.classifier);
  const subject = encodeURIComponent(
    `Property insurance — ${reason.subject}`
  );
  const body = encodeURIComponent(
    [
      'Hi Derek,',
      '',
      reason.bodyIntro,
      '',
      'A few details so you can prepare:',
      '• Applicant name:',
      '• Address(es) / schedule:',
      '• Coverages needed:',
      '• Approximate TIV:',
      '',
      'Thanks!',
    ].join('\n')
  );
  const mailto = `mailto:dkrenke@zacharycompanies.com?cc=aminus@zacharycompanies.com&subject=${subject}&body=${body}`;

  return (
    <>
      <Navbar />
      <main className="container-site py-20">
        <div className="flex flex-col gap-10 max-w-3xl">
          <StatusChip
            label="PROPERTY_INSURANCE · BROKER_ASSIST"
            tone="active"
          />
          <h1 className="font-display font-medium tracking-[-0.02em] leading-[1.05] text-[clamp(36px,5vw,56px)]">
            {reason.headline}
          </h1>
          <p className="max-w-prose text-ink-muted text-[1.0625rem] leading-relaxed">
            {reason.body}
          </p>

          <Card className="p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle">
                DIRECT &middot; DEREK_KRENKE
              </span>
              <span className="font-display text-[1.0625rem] text-ink">
                dkrenke@zacharycompanies.com
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle">
                CC &middot; ART_MINUS
              </span>
              <span className="font-display text-[1.0625rem] text-ink">
                aminus@zacharycompanies.com
              </span>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button as="a" href={mailto} variant="primary" size="md">
                Open a draft email
              </Button>
              <Button as="link" to="/property" variant="secondary" size="md">
                Back to classifier
              </Button>
            </div>
          </Card>

          <p className="text-[0.8125rem] text-ink-subtle">
            Want to keep browsing?{' '}
            <Link to="/" className="underline decoration-1 underline-offset-2">
              Return home
            </Link>
            .
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}

function buildReason(classifier: Classifier | undefined): {
  headline: string;
  subject: string;
  bodyIntro: string;
  body: string;
} {
  if (classifier?.portfolioScope === 'multiple') {
    return {
      headline: 'Portfolio submissions are broker-assisted in v1.',
      subject: 'Portfolio schedule — please reach out',
      bodyIntro:
        "I'm operating a portfolio of multifamily buildings and would like a property-insurance quote.",
      body: "You've told us this quote covers more than one building. The self-serve tool is single-location for v1 — Derek and Art will reach out and handle the schedule by email, same turnaround as a self-serve submission.",
    };
  }
  if (classifier?.tivBucket === '100M+') {
    return {
      headline: 'Large accounts are broker-first.',
      subject: 'Large habitational submission',
      bodyIntro:
        "I'm evaluating property insurance for a $100M+ habitational account.",
      body: "For accounts above $100M in total insurable value, Derek and Art want to handle the submission directly — carrier appetite and structure vary at that scale. A direct email is the fastest path; they'll respond within two business days.",
    };
  }
  return {
    headline: 'Get in touch with a broker.',
    subject: 'Property insurance inquiry',
    bodyIntro: "I'd like to talk about property insurance for a multifamily building.",
    body: "Reach out directly and a broker will follow up within two business days.",
  };
}

export default PropertyContactBroker;
